import { useCallback, useState } from 'react'
import { useResetRecoilState } from 'recoil'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { taskQueueState } from '../store'
import { useAnalytics } from './useAnalytics'

// const fetchOptions = ({ session, body, ...options }) => ({
// 	headers: {
// 		'Content-Type': 'application/json',
// 	},
// 	mode: 'cors',
// 	body: body ? JSON.stringify(body) : undefined,
// 	...(options || {}),
// })

export const clearStorage = () => {
	try {
		localStorage.removeItem('user')
		sessionStorage.removeItem('user')
		localStorage.removeItem('session-token')
		sessionStorage.removeItem('session-token')
	} catch (e) {}
}

const updateStorage = (user, token, rememberMe) => {
	try {
		const sessionUser = {
			...user,
			roles: user.roles.map((r) => r.toLowerCase()),
		}
		if (rememberMe) {
			sessionStorage.removeItem('user')
			localStorage.setItem('user', JSON.stringify(sessionUser))
			if (token) {
				sessionStorage.removeItem('session-token')
				localStorage.setItem('session-token', token)
			}
		} else {
			localStorage.removeItem('user')
			sessionStorage.setItem('user', JSON.stringify(sessionUser))
			if (token) {
				localStorage.removeItem('session-token')
				sessionStorage.setItem('session-token', token)
			}
		}
	} catch (_) {
		console.error(_)
	}
}

const useSession = () => {
	const storageUser =
		localStorage.getItem('user') || sessionStorage.getItem('user') || null
	const userData = JSON.parse(storageUser)
	const initialState = { user: userData, activity: false, error: null }
	const [sessionInfo, setSessionInfo] = useState(initialState)
	const resetTaskQueue = useResetRecoilState(taskQueueState)
	const { ampliIdentify, ampliFlush } = useAnalytics()

	const { user, activity, error } = sessionInfo

	const setSessionUser = (userAccount, token, rememberMe) => {
		updateStorage(userAccount, token, rememberMe)
		setSessionInfo(() => ({
			user: userAccount,
			activity: false,
			error: false,
		}))
	}

	const login = (username, password, rememberMe) => {
		setSessionInfo((prev) => ({
			...prev,
			activity: true,
			error: null,
		}))
		return fetch(
			`${API_URL}/api/session`,
			fetchOptions({
				method: 'POST',
				body: { username, password },
			})
		)
			.then((r) => parseJSONContent(r))
			.then((response) => {
				if (response) {
					const { token, account } = response
					const user = {
						...(account || {}),
						id: account.id,
						username: account.username,
						firstName: account.firstName,
						lastName: account.lastName,
						avatar: account.avatar,
						changePasswordRequired: account.changePasswordRequired,
						rememberMe,
					}
					setSessionUser(user, token, rememberMe)
					ampliIdentify(user.username, {
						idUtilizador: user.username,
						nomeUtilizador: [user?.firstName, user?.lastName].join(
							' '
						),
						rolesUtilizador: user.roles,
					})
					return user
				} else {
					setSessionInfo((prev) => ({
						...prev,
						activity: false,
						error: { message: 'Invalid credentials' },
					}))
				}
			})
			.catch((e) => {
				setSessionInfo((prev) => ({
					...prev,
					activity: false,
					error: e,
				}))
			})
	}

	const logout = useCallback(() => {
		// TODO: reset all recoil data
		resetTaskQueue()
		resetState(true, true, true)
		ampliFlush()

		try {
			clearStorage()
		} catch (_) {}
		setTimeout(() => {
			window.location.reload()
		}, 100)
	}, [ampliFlush, resetTaskQueue])

	const changePassword = (passwords, restoreSession) => {
		if (passwords.newPassword !== passwords.newPasswordConfirmation) {
			setSessionInfo((prev) => ({
				...prev,
				activity: false,
				error: {
					message:
						'A password de confirmação não coincide com a nova password',
				},
			}))
			return Promise.reject()
		} else {
			return fetch(
				`${API_URL}/api/session/change_password`,
				fetchOptions({
					method: 'POST',
					body: {
						oldPassword: passwords.oldPassword,
						newPassword: passwords.newPassword,
					},
				})
			)
				.then((r) => parseJSONContent(r))
				.then((response) => {
					if (response) {
						const { account, token } = response || {}
						//TODO: user server response
						setSessionUser(
							{
								...(account || user),
								changePasswordRequired: false,
							},
							token,
							user.rememberMe
						)
					} else {
						setSessionInfo((prev) => ({
							...prev,
							activity: false,
							error: {
								message:
									'Aconteceu um erro. Por favor tente mais tarde.',
							},
						}))
					}
					return true
				})
				.catch((e) => {
					setSessionInfo((prev) => ({
						...prev,
						activity: false,
						error: e,
					}))
					return false
				})
		}
	}

	const resetState = (user, activity, error) => {
		setSessionInfo((prev) => ({
			user: user ? undefined : prev.user,
			activity: activity ? undefined : prev.activity,
			error: error ? undefined : prev.error,
		}))
	}

	return {
		user,
		activity,
		error,
		login,
		logout,
		changePassword,
		resetState,
	}
}

export default useSession
